import { Member } from "./Member";
import { Plan, PlanVariant } from "./Subscription";
import { Contact } from "./Contact";
import type { BookingSlotsConfig } from "./Room";


export enum StripeSubscriptionStatus {
    INCOMPLETE = 'INCOMPLETE',
    INCOMPLETE_EXPIRED = 'INCOMPLETE_EXPIRED',
    TRIALING = 'TRIALING',
    ACTIVE = 'ACTIVE',
    PAST_DUE = 'PAST_DUE',
    /** Stripe spelling; matches OpenAPI `CANCELED`. */
    CANCELED = 'CANCELED',
    UNPAID = 'UNPAID',
    PAUSED = 'PAUSED'
}

export enum PaymentMethod {
    CASH = 'CASH',
    ONLINE = 'ONLINE'
}

export enum OrganizationType {
    /** Legacy seed / transitional value; treat like REHEARSAL_STUDIO where product rules allow. */
    BUSINESS = 'BUSINESS',
    INDIVIDUAL_TUTOR = 'INDIVIDUAL_TUTOR',
    REHEARSAL_STUDIO = 'REHEARSAL_STUDIO',
    MUSIC_SCHOOL = 'MUSIC_SCHOOL',
}

export interface AutoConfirmSettings {
    cash: boolean;
    online: boolean;
}

export interface StripeConnectExpressAccount {
    id: string;
    onboardingUrl?: string;
    accountDashboardUrl?: string;
    actionRequired: boolean;
    detailsSubmitted: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
}

/** Stripe Connect Express payout (bank transfer) for a connected account. */
export interface StripePayout {
    id: string;
    status: string;
    currencyCode: string | null;
    /** Stripe minor units (e.g. groszy for PLN). */
    amount: number;
    createdAt: string | null;
    arrivalAt: string | null;
    automatic: boolean;
    description: string | null;
    failureCode: string | null;
    failureMessage: string | null;
}

export interface StripePayoutPage {
    past: StripePayout[];
    upcoming: StripePayout[];
    hasMore: boolean;
    nextStartingAfter: string | null;
}

export type GetStripePayoutsParams = {
    limit?: number;
    startingAfter?: string;
};

/** Formats Stripe payout minor units as a major currency amount. */
export function formatStripePayoutAmount(
    amountMinor: number,
    currencyCode: string | null | undefined,
    locale?: string,
): string {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: (currencyCode || 'PLN').toUpperCase(),
    }).format(amountMinor / 100);
}

export interface JoinInfo {
    organizationName: string;
}

export interface TutorInvitationInfo {
    organizationName: string;
    spaceName?: string | null;
}

export interface SingleBookingDiscountSettings {
    enabled: boolean;
    minimumBookingHours: number;
    discountPercent: number;
}

export interface DiscountSettings {
    singleBookingDiscount?: SingleBookingDiscountSettings | null;
}

/** Org feature gate for student lesson attendance confirmation. Default: enabled false. */
export interface ConfirmAttendanceSettings {
    enabled: boolean;
}

export interface Organization {
    id: string;
    name: string;
    members: Member[];
    createdAt: string;
    updatedAt: string;
    joinCode: string;
    enabled: boolean;
    googleCalendarConnected: boolean;
    /** Room booking payment methods. */
    supportedPaymentMethods: PaymentMethod[];
    /** Tutor booking payment methods (independent of room methods). */
    tutorSupportedPaymentMethods?: PaymentMethod[];
    autoConfirmSettings?: AutoConfirmSettings;
    /**
     * When true, newly created cash room and tutor bookings are marked PAID immediately.
     * Separate from autoConfirmSettings.cash (confirmation vs payment collection).
     * Default false: cash bookings stay TO_BE_PAID_ON_SITE until collect-payment.
     */
    autoCollectCashPayments?: boolean;
    /** Flattened Stripe Connect Express fields from admin/host org payloads. */
    stripeConnectExpressAccountId?: string | null;
    stripeConnectExpressChargesEnabled?: boolean;
    stripeConnectExpressPayoutsEnabled?: boolean;
    stripeConnectExpressDetailsSubmitted?: boolean;
    stripeConnectExpressAccountActionRequired?: boolean;
    stripeCustomerPortalUrl?: string;
    stripeSubscriptionCheckoutSessionId?: string;
    stripeSubscriptionStatus?: StripeSubscriptionStatus;
    stripeSubscriptionCurrentPeriodEnd?: string;
    stripeSubscriptionCancelAtPeriodEnd: boolean;
    plan?: Plan;
    planVariant?: PlanVariant;
    cancellationDeadlineHours: number;
    refundPercentageBeforeDeadline: number;
    refundPercentageAfterDeadline: number;
    contacts?: Contact[];
    discountSettings?: DiscountSettings | null;
    /**
     * Org-wide booking slot constraints (room bookings and tutor lessons).
     * Null means unrestricted start/duration (legacy behavior).
     */
    bookingSlotsConfig?: BookingSlotsConfig | null;
    /**
     * Organization T&C (text or URL). Mirrored on space detail payloads as derived `regulation`.
     * When non-empty, booking forms require regulationAccepted.
     */
    regulation?: string | null;
    /** Org-scoped experimental features (GET /host/organization; admin list may include this). */
    experimentalFeaturesEnabled?: boolean;
    /**
     * Whether Confirm Attendance is available for this organization.
     * Separate from experimentalFeaturesEnabled. Missing/null → treat as enabled: false.
     */
    confirmAttendanceSettings?: ConfirmAttendanceSettings | null;
    /** Test organizations are hidden from public search unless X-Allow-Test-Orgs is set. */
    test?: boolean;
    /** Drives host vs tutor Stripe Connect routing. */
    organizationType?: OrganizationType;
}